import { useState, useRef, useCallback } from 'react'
import { Camera, X, Upload, Loader2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'

interface ImageUploadProps {
  currentImageUrl?: string | null
  onUpload: (url: string) => void
  onRemove?: () => void
  uploadEndpoint: string
  fieldName?: string
  placeholder?: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export function ImageUpload({
  currentImageUrl,
  onUpload,
  onRemove,
  uploadEndpoint,
  fieldName = 'photo',
  placeholder,
  className = '',
  size = 'md',
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sizeClasses = {
    sm: 'image-upload--sm',
    md: 'image-upload--md',
    lg: 'image-upload--lg',
  }

  const handleFileSelect = useCallback(async (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use JPEG, PNG, WebP o GIF.')
      return
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      setError('La imagen es demasiado grande. Máximo 5MB.')
      return
    }

    // Create preview
    const preview = URL.createObjectURL(file)
    setPreviewUrl(preview)
    setError(null)
    setIsUploading(true)

    try {
      const result = await api.upload<{ url: string }>(uploadEndpoint, file, fieldName)
      onUpload(result.url)
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Error al subir la imagen'
      setError(message)
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
      URL.revokeObjectURL(preview)
    }
  }, [uploadEndpoint, fieldName, onUpload])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    // Reset input to allow selecting the same file again
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPreviewUrl(null)
    setError(null)
    onRemove?.()
  }

  const displayUrl = previewUrl || currentImageUrl

  return (
    <div className={`image-upload ${sizeClasses[size]} ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleInputChange}
        className="image-upload__input"
        aria-label="Subir imagen"
      />

      <div
        className={`image-upload__area ${isUploading ? 'image-upload__area--uploading' : ''}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      >
        {isUploading ? (
          <div className="image-upload__loading">
            <Loader2 className="image-upload__spinner" />
            <span>Subiendo...</span>
          </div>
        ) : displayUrl ? (
          <div className="image-upload__preview">
            <img src={displayUrl} alt="Preview" />
            <div className="image-upload__overlay">
              <Camera size={24} />
              <span>Cambiar</span>
            </div>
            {onRemove && (
              <button
                type="button"
                className="image-upload__remove"
                onClick={handleRemove}
                aria-label="Eliminar imagen"
              >
                <X size={16} />
              </button>
            )}
          </div>
        ) : (
          <div className="image-upload__placeholder">
            {placeholder || (
              <>
                <Upload size={32} />
                <span>Subir imagen</span>
                <span className="image-upload__hint">Arrastra o haz clic</span>
              </>
            )}
          </div>
        )}
      </div>

      {error && <p className="image-upload__error">{error}</p>}
    </div>
  )
}
